import { useState, useEffect } from "react";
import { Instagram, Youtube, Twitter, Facebook, Github, Linkedin, Twitch, Music2, Globe, ExternalLink, Play, Copy, CheckCircle2, Tag, Star, ShoppingBag, Timer, ChevronDown, Send, HelpCircle, MessageCircle, Calendar, MapPin, Mail, Handshake, Lock, Loader2, Navigation, Ghost, DiscAlbum, ArrowUpRight } from "lucide-react";
import "leaflet/dist/leaflet.css";
import BrandCollabBlock from "./BrandCollabBlock";
import ContactCardBlock from "./actionBlocks/ContactCardBlock";
import ImageGridBlock from "./actionBlocks/ImageGridBlock";
import VideoGridBlock from "./actionBlocks/VideoGridBlock";
import { EventBlock, TicketBookingBlock, RegistrationBlock, SubscriptionBlock } from "./CommunityBlocks";
import { LocationConsentBlock } from "./LocationConsentBlock";
import PhoneInput from "./PhoneInput";
import WhatsAppIcon from "./icons/WhatsAppIcon";
// v113 — Storefront block reuses the SAME card + buy modal as /store/{slug}
// so services, coupons, address, OTP, instructions, deals — every option
// the standalone page offers — works identically when the block is embedded
// in a bio page or an action page.
import { StorefrontCard, StorefrontBuyModal } from "../pages/Storefront";
// v145.27 — Multi-product cart provider + sticky cart bar for the
// storefront block on bio hubs / action pages.
import { StorefrontCartProvider } from "./commerce/StorefrontCartContext";
import StorefrontCartBar from "./commerce/StorefrontCartBar";
import { InlineVideoEmbed } from "./InlineVideoEmbed";
import { isYoutubeUrl } from "./actionBlocks/_shared";
// v146.22 — Wraps lead/appointment/enquiry forms behind a proper CTA
// button so published pages stay clean until the visitor opts in.
import CollapsibleFormWrap from "./CollapsibleFormWrap";
// v146.32 — Isolates iframe/embed crashes (e.g. Spotify RangeError) so a
// single misbehaving block cannot unmount the whole preview tree.
import BlockErrorBoundary from "./BlockErrorBoundary";
import { sizedImage } from "../lib/imageUrl";

/**
 * `ActionBlockRenderer` — one component drives both the in-app editor preview
 * and the live public page. Themes are controlled by the parent's
 * `.ah-theme.ah-<theme>` scope; blocks consume CSS variables (see
 * `styles/action-hub-themes.css`), so switching themes swaps colors instantly
 * without touching this file.
 *
 * `onClick(block, href)` fires whenever a link/video/socials tap should be
 * tracked. The public page passes a click-tracking impl; the editor passes a
 * no-op preventDefault to keep users inside the app.
 */

const SOCIAL_ICONS = {
  instagram: Instagram,
  youtube: Youtube,
  twitter: Twitter,
  x: Twitter,
  facebook: Facebook,
  github: Github,
  linkedin: Linkedin,
  twitch: Twitch,
  tiktok: Music2,
  spotify: Music2,
  snapchat: Ghost,
  whatsapp: WhatsAppIcon,
  telegram: TelegramIcon,
  website: Globe,
};

// FontAwesome-style Telegram brand mark. Lucide ships a Send icon but the
// filled paper-plane silhouette is what people recognise. Same inline-SVG
// pattern used for WhatsApp below so we don't balloon the bundle.
function TelegramIcon({ className, style }) {
  return (
    <svg
      viewBox="0 0 496 512"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M248 8C111 8 0 119 0 256s111 248 248 248 248-111 248-248S385 8 248 8zm121.8 169.9l-40.7 191.8c-3 13.6-11.1 16.9-22.4 10.5l-62-45.7-29.9 28.8c-3.3 3.3-6.1 6.1-12.5 6.1l4.4-63.1 114.9-103.8c5-4.4-1.1-6.9-7.7-2.5l-142 89.4-61.2-19.1c-13.3-4.2-13.6-13.3 2.8-19.7l239.1-92.2c11.1-4 20.8 2.7 17.2 19.5z"/>
    </svg>
  );
}



function socialIcon(platform) {
  const key = (platform || "").toLowerCase();
  return SOCIAL_ICONS[key] || Globe;
}

// ---------------------------------------------------------------------------
// v146.45 — Spotify rich embed item (album / track / artist / playlist / show
// / episode). Uses Spotify's `/embed/{kind}/{id}` iframe which ships a mini
// player with cover art + 30-sec preview. Two safety nets:
//   1. `?locale=en_US` forces Spotify's Next.js shell to skip the
//      `Intl.NumberFormat('en-IN', …)` call that crashes with `RangeError`
//      on regional English locales — the root cause of the v146.37 fallback
//      that replaced the rich card with a plain button.
//   2. `onError` on the iframe swaps in the branded green fallback card so
//      the block still looks intentional if the iframe truly fails to load
//      (Content-Security-Policy, offline, ad-block, etc.).
// ---------------------------------------------------------------------------
function SpotifyEmbedItem({ index, block, raw, parsed, editable, handleTrackedNav, kindLabel, openLabel }) {
  const [meta, setMeta] = useState(null);
  const openUrl = `https://open.spotify.com/${parsed.kind}/${parsed.id}`;
  const title = (raw?.title || "").trim();

  // v146.45 — Spotify's `/embed/*` iframe crashes on many browsers
  // with `RangeError: Incorrect locale information provided` inside
  // their own Next.js shell — a bug on Spotify's side that we can't
  // patch from outside. Instead we fetch their public oEmbed JSON
  // (title + album-cover thumbnail_url + provider) directly from the
  // browser (CORS-enabled: `Access-Control-Allow-Origin: *`) and
  // render our own rich card. Falls back to a plain green tile if
  // oEmbed itself hiccups.
  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    const t = setTimeout(() => ac.abort(), 6000);
    (async () => {
      try {
        const r = await fetch(
          `https://open.spotify.com/oembed?url=${encodeURIComponent(openUrl)}`,
          { signal: ac.signal, mode: "cors" },
        );
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled || !d?.thumbnail_url) return;
        setMeta({
          title: d.title || "",
          thumbnail_url: d.thumbnail_url,
        });
      } catch { /* silent — fallback card renders */ }
      finally { clearTimeout(t); }
    })();
    return () => { cancelled = true; ac.abort(); clearTimeout(t); };
  }, [openUrl]);

  return (
    <a
      href={openUrl}
      target={editable ? undefined : "_blank"}
      rel="noreferrer"
      onClick={(e) => handleTrackedNav(e, openUrl)}
      className="ah-block flex items-center gap-3 p-2 sm:p-3"
      style={{ textDecoration: "none" }}
      data-testid={`block-spotify-item-${block.block_id}-${index}`}
    >
      {meta?.thumbnail_url ? (
        <img
          src={meta.thumbnail_url}
          alt={meta.title || "Spotify cover art"}
          loading="lazy"
          className="flex-shrink-0 border-2 border-ink"
          style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8 }}
          data-testid={`block-spotify-cover-${block.block_id}-${index}`}
        />
      ) : (
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{ width: 64, height: 64, borderRadius: 8, background: "#1DB954", color: "#000" }}
        >
          <DiscAlbum className="w-7 h-7" strokeWidth={2.5} />
        </div>
      )}
      <div className="flex-1 min-w-0 text-left">
        <div className="text-[10px] uppercase tracking-wider ah-muted font-semibold flex items-center gap-1">
          <DiscAlbum className="w-3 h-3" strokeWidth={2.5} />
          Spotify · {kindLabel[parsed.kind] || "Track"}
        </div>
        <div
          className="text-sm font-bold ah-fg truncate mt-0.5"
          data-testid={`block-spotify-title-${block.block_id}-${index}`}
        >
          {title || meta?.title || openLabel[parsed.kind] || "Play on Spotify"}
        </div>
        <div className="text-[11px] ah-muted mt-0.5">Tap to play in Spotify</div>
      </div>
      <div
        className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full flex-shrink-0"
        style={{ background: "#1DB954", color: "#000" }}
        data-testid={`block-spotify-follow-${block.block_id}-${index}`}
      >
        Play
      </div>
    </a>
  );
}



export default function ActionBlockRenderer({ block, onClick, editable = false, hubContext = null, onGateUnlock = null }) {
  const c = block?.config || {};
  const type = block?.type;
  // v147.16 — Global feature flags for the Swiggy / Zomato (/ Ownly, v149.67) order blocks.
  // When super-admin flips either flag off, existing blocks stop rendering
  // on public pages so a rollback is instant with no per-page migration.
  const { flag } = useAppFlags();
  const handleTrackedNav = (e, href) => {
    if (editable) { e.preventDefault(); return; }
    if (onClick) onClick(block, href);
  };

  if (type === "link") {
    // v146.21 — Refreshed layout: title + optional description on the
    // left, a proper "Visit" pill button on the right. Old bio pages
    // that only had a `label` still render fine (description hidden).
    const btnLabel = c.button_label || "Visit";
    return (
      <a
        href={c.url || "#"}
        target={editable ? undefined : "_blank"}
        rel="noreferrer"
        onClick={(e) => handleTrackedNav(e, c.url)}
        className="ah-block flex items-center gap-3 group"
        data-testid={`block-link-${block.block_id}`}
      >
        <div className="flex-1 min-w-0">
          <p className="font-bold text-base leading-tight">{c.label || "Untitled link"}</p>
          {(c.description || c.subtitle) && (
            <p className="text-xs ah-muted leading-snug mt-0.5 line-clamp-2">
              {c.description || c.subtitle}
            </p>
          )}
        </div>
        <span
          className="inline-flex items-center gap-1 shrink-0 border-2 border-current px-3 py-1.5 text-xs font-black uppercase tracking-wider group-hover:bg-current group-hover:text-white transition-colors"
          data-testid={`block-link-cta-${block.block_id}`}
        >
          {btnLabel} <ExternalLink className="w-3 h-3" strokeWidth={2.5} />
        </span>
      </a>
    );
  }

  if (type === "header") {
    const size = c.size === "sm" ? "text-lg" : c.size === "xl" ? "text-3xl" : "text-2xl";
    // v146.20 — Honour `c.align` (left / center / right). Default stays
    // "center" so unchanged headers keep looking exactly as they were.
    const align = (c.align || "center").toLowerCase();
    const alignCls = align === "left" ? "text-left"
                   : align === "right" ? "text-right"
                   : "text-center";
    return (
      <h2 className={`ah-title ${alignCls} ${size}`} data-testid={`block-header-${block.block_id}`}>
        {c.text || "Header"}
      </h2>
    );
  }

  if (type === "text") {
    // v146.5 — Wrap in `.ah-block` so the text sits inside a themed
    // card (bg `var(--ah-block-bg)`, fg `var(--ah-block-fg)`). Before
    // this, a bare `<p>` sat directly on the page bg, and on the neon /
    // dark themes the previous `.ah-muted` colour (55% alpha over the
    // near-black page) rendered as an invisible smudge. The alignment
    // field is honoured; default remains centred to match the old look.
    const align = (c.align || "center").toLowerCase();
    const alignCls = align === "left" ? "text-left"
                   : align === "right" ? "text-right"
                   : "text-center";
    return (
      <div
        className={`ah-block ${alignCls}`}
        data-testid={`block-text-${block.block_id}`}
      >
        <p
          className={`text-sm leading-relaxed whitespace-pre-wrap ${alignCls}`}
          style={{ color: "var(--ah-block-fg)" }}
        >
          {c.text || ""}
        </p>
      </div>
    );
  }

  if (type === "image") {
    const img = (
      <img
        src={sizedImage(c.url, 1280)}
        alt={c.alt || ""}
        className="w-full block"
        style={{ borderRadius: "var(--ah-block-radius)" }}
        data-testid={`block-image-${block.block_id}`}
        loading="lazy"
        decoding="async"
      />
    );
    if (!c.url) return <div className="ah-block text-center text-xs ah-muted">Image (no URL set)</div>;
    if (c.link) {
      return (
        <a
          href={c.link}
          target={editable ? undefined : "_blank"}
          rel="noreferrer"
          onClick={(e) => handleTrackedNav(e, c.link)}
          className="block"
          data-testid={`block-image-link-${block.block_id}`}
        >
          {img}
        </a>
      );
    }
    return img;
  }

  if (type === "hero_image") {
    if (!c.url) return <div className="ah-block text-center text-xs ah-muted">Hero image (no URL set)</div>;
    // Only darken the image when text/CTA is layered on top of it. Bare
    // hero images (no headline / subtitle / cta) render at full opacity
    // so the photo stays sharp. When content IS overlaid, we clamp the
    // opacity to a sensible range (10–60%) so the image never goes fully
    // opaque (which would hide the text) but also never gets washed out.
    const hasOverlayContent = !!(
      c.eyebrow || c.headline || c.subtitle || (c.cta_label && (c.cta_url || c.cta_block_id))
    );
    const rawOverlay = c.overlay_opacity !== undefined ? Number(c.overlay_opacity) : 30;
    const overlayOpacity = hasOverlayContent
      ? Math.max(10, Math.min(60, rawOverlay))
      : 0;
    const align = c.align || "center"; // left, center, right
    const alignClass = align === "left" ? "text-left items-start" : align === "right" ? "text-right items-end" : "text-center items-center";
    const paddingClass = "px-6 sm:px-10 py-8";
    // v147.40 — Every hero image (bare AND overlay-content) now renders
    // at its NATURAL aspect ratio. Previously overlay banners were
    // pinned to a fixed 26rem height with `object-cover`, which
    // cropped wide artwork on the left / right and left-aligned it
    // inside the preview frame. New behaviour:
    //   * <img> uses `block w-full h-auto` — width matches the card,
    //     height auto-scales to preserve the image's own ratio.
    //   * Overlay text (eyebrow/headline/subtitle/CTA) is layered on
    //     top via `absolute inset-0` with a soft gradient so it stays
    //     readable regardless of image tone.
    // Result: uploads keep their designer-approved framing across
    // preview + live and mobile + desktop.
    const HeroContent = (
      <div
        className="relative w-full overflow-hidden"
        style={{ borderRadius: "var(--ah-block-radius)" }}
        data-testid={`block-hero-image-${block.block_id}`}
        data-hero-bare={hasOverlayContent ? undefined : "1"}
      >
        <img
          src={sizedImage(c.url, 1280)}
          alt={c.alt || c.headline || ""}
          className="block w-full h-auto max-w-full"
          loading="lazy"
          decoding="async"
        />
        {hasOverlayContent && (
          <>
            <div
              className="absolute inset-0"
              style={{ background: `linear-gradient(135deg, rgba(0,0,0,${overlayOpacity / 100}) 0%, rgba(0,0,0,${(overlayOpacity + 15) / 100}) 100%)` }}
            />
            <div className={`absolute inset-0 flex flex-col justify-center ${alignClass} ${paddingClass} w-full text-white`}>
              {c.eyebrow && (
                <p className="text-[11px] font-black tracking-[0.2em] uppercase mb-3 opacity-90">{c.eyebrow}</p>
              )}
              {c.headline && (
                <h2 className="font-black text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight mb-3 drop-shadow-lg">
                  {c.headline}
                </h2>
              )}
              {c.subtitle && (
                <p className="text-sm sm:text-base mb-4 opacity-90 max-w-lg drop-shadow-md">{c.subtitle}</p>
              )}
              {c.cta_label && (c.cta_url || c.cta_block_id) && (
                c.cta_block_id ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.querySelector(`[data-block-anchor="${c.cta_block_id}"]`);
                      if (el && typeof el.scrollIntoView === "function") {
                        el.scrollIntoView({ behavior: "smooth", block: "start" });
                        el.classList.add("ring-4", "ring-coral");
                        setTimeout(() => el.classList.remove("ring-4", "ring-coral"), 1600);
                      }
                    }}
                    className="inline-flex items-center gap-1.5 bg-white text-black font-bold text-sm px-5 py-3 border-2 border-white hover:bg-black hover:text-white hover:border-white transition-all shadow-lg self-center"
                    data-testid={`block-hero-cta-${block.block_id}`}
                  >
                    {c.cta_label} ↓
                  </button>
                ) : (
                  <a
                    href={c.cta_url}
                    target={editable ? undefined : "_blank"}
                    rel="noreferrer"
                    onClick={(e) => handleTrackedNav(e, c.cta_url)}
                    className="inline-flex items-center gap-1.5 bg-white text-black font-bold text-sm px-5 py-3 border-2 border-white hover:bg-black hover:text-white hover:border-white transition-all shadow-lg self-center"
                    data-testid={`block-hero-cta-${block.block_id}`}
                  >
                    {c.cta_label} →
                  </a>
                )
              )}
            </div>
          </>
        )}
      </div>
    );
    return HeroContent;
  }

  if (type === "video") {
    // v134 — Delegate rendering to the shared InlineVideoEmbed which
    // supports YouTube (long + shorts), Instagram (reel/p/tv), TikTok,
    // and direct MP4/WebM files. Falls back to a "Watch on <platform>"
    // link when the URL isn't embeddable.
    if (c.url) {
      return (
        <InlineVideoEmbed
          url={c.url}
          testid={`block-video-${block.block_id}`}
          className="border-none"
          blockId={block.block_id}
          productId={c.product_id || null}
          couponId={c.coupon_id || null}
          surface="bio_block"
          // v143.13 (Feb 2026) — When the video actually plays, also
          // log a `click` event to the shared action_page_events
          // pipeline so per-block Results reflects the interaction.
          // Prior state: video plays were only recorded in the
          // separate `/analytics/video/event` table which the Results
          // drawer doesn't read, so every video block showed 0.
          onPlay={() => { if (onClick) onClick(block, c.url); }}
          // v149.5 — Cover / thumbnail. `thumbnail_url` is the creator's
          // optional upload from the block editor; `cover` turns on the
          // automatic fallback (YouTube thumb, else our own play-card,
          // else a first-frame hint for self-hosted files) so the block
          // is never a blank rectangle before playback.
          posterUrl={c.thumbnail_url || null}
          cover
        />
      );
    }
    return (
      <a
        href="#"
        onClick={(e) => { e.preventDefault(); }}
        className="ah-block flex items-center justify-center gap-2"
        data-testid={`block-video-${block.block_id}`}
      >
        <Play className="w-4 h-4" strokeWidth={2.5} /> <span className="font-bold">{c.label || "Add a video URL"}</span>
      </a>
    );
  }

  if (type === "socials") {
    const items = (c.items || []).filter((i) => i && i.url && i.platform);
    if (!items.length) return <p className="text-xs ah-muted text-center" data-testid={`block-socials-empty-${block.block_id}`}>No socials added</p>;
    return (
      <div className="flex items-center justify-center gap-3 flex-wrap" data-testid={`block-socials-${block.block_id}`}>
        {items.map((it, i) => {
          const Icon = socialIcon(it.platform);
          return (
            <a
              key={i}
              href={it.url}
              target={editable ? undefined : "_blank"}
              rel="noreferrer"
              onClick={(e) => handleTrackedNav(e, it.url)}
              className="flex items-center justify-center ah-block"
              style={{ padding: 0, width: "2.75rem", height: "2.75rem", flex: "0 0 auto", display: "inline-flex" }}
              aria-label={it.platform}
              data-testid={`block-socials-item-${block.block_id}-${i}`}
            >
              <Icon className="w-5 h-5" strokeWidth={2.5} />
            </a>
          );
        })}
      </div>
    );
  }

  if (type === "spotify_album") {
    // Parse any Spotify URL/URI into { kind, id } so we can build a stable
    // embed URL. Supports open.spotify.com/{kind}/{id}?..., embed URLs,
    // and spotify:{kind}:{id} URIs. Kind falls back to whatever the user
    // picked in the editor if the URL doesn't include one.
    const ALLOWED_KINDS = new Set(["album", "track", "artist", "playlist", "show", "episode"]);
    const parseSpotify = (raw, fallbackKind = "album") => {
      const url = (raw || "").trim();
      if (!url) return null;
      // spotify:album:xyz
      let m = url.match(/^spotify:(album|track|artist|playlist|show|episode):([a-zA-Z0-9]+)/i);
      if (m) return { kind: m[1].toLowerCase(), id: m[2] };
      // https://open.spotify.com/embed/album/xyz  OR  /album/xyz  (with optional locale prefix)
      m = url.match(/open\.spotify\.com(?:\/[a-zA-Z-]+)?\/(?:embed\/)?(album|track|artist|playlist|show|episode)\/([a-zA-Z0-9]+)/i);
      if (m) return { kind: m[1].toLowerCase(), id: m[2] };
      // Plain ID? Use the fallback kind picked in the editor.
      if (/^[a-zA-Z0-9]{15,}$/i.test(url) && ALLOWED_KINDS.has(fallbackKind)) {
        return { kind: fallbackKind, id: url };
      }
      return null;
    };
    const items = (c.items || [])
      .map((it) => ({ raw: it, parsed: parseSpotify(it?.url, it?.kind) }))
      .filter((x) => x.parsed);
    if (!items.length) {
      return <p className="text-xs ah-muted text-center" data-testid={`block-spotify-empty-${block.block_id}`}>Add a Spotify link to show it here.</p>;
    }
    // v146.45 — Restore the rich Spotify embed (with preview play + cover
    // art) that was dropped in v146.37. That fix removed the iframe to
    // dodge a `RangeError: Incorrect locale information` crash inside
    // Spotify's Next.js shell on `en-IN` browsers. Spotify's iframe
    // honours a `locale=en_US` URL override — passing it skips the
    // Intl code path that was blowing up, so we get the rich card back
    // without leaking "Application error" onto the page. The plain
    // green fallback below still renders when a link can't be parsed
    // or if the iframe fails to load (`onError`).
    const kindLabel = {
      album: "Album",
      track: "Song",
      artist: "Artist",
      playlist: "Playlist",
      show: "Show",
      episode: "Episode",
    };
    const openLabel = {
      album: "Play album on Spotify",
      track: "Play song on Spotify",
      artist: "Follow on Spotify",
      playlist: "Play playlist on Spotify",
      show: "Follow show on Spotify",
      episode: "Play on Spotify",
    };
    return (
      <div className="space-y-3" data-testid={`block-spotify-${block.block_id}`}>
        {c.headline ? (
          <div className="flex items-center gap-2 justify-center text-sm font-bold ah-fg" data-testid={`block-spotify-headline-${block.block_id}`}>
            <DiscAlbum className="w-4 h-4" strokeWidth={2.5} />
            <span>{c.headline}</span>
          </div>
        ) : null}
        {items.map(({ raw, parsed }, i) => (
          <SpotifyEmbedItem
            key={i}
            index={i}
            block={block}
            raw={raw}
            parsed={parsed}
            editable={editable}
            handleTrackedNav={handleTrackedNav}
            kindLabel={kindLabel}
            openLabel={openLabel}
          />
        ))}
      </div>
    );
  }

  if (type === "embed") {
    if (c.url && isYoutubeUrl(c.url)) {
      // v149.19 — Same treatment as the `video` block: a cover + play gate
      // instead of a YouTube iframe mounted on page load. One less
      // third-party frame competing with the scroll on first paint, and
      // the block shows a thumbnail instead of an empty player.
      return (
        <InlineVideoEmbed
          url={c.url}
          testid={`block-embed-${block.block_id}`}
          blockId={block.block_id}
          surface="bio_block"
          posterUrl={c.thumbnail_url || null}
          cover
        />
      );
    }
    if (c.url) {
      return (
        <a
          href={c.url}
          target={editable ? undefined : "_blank"}
          rel="noreferrer"
          onClick={(e) => handleTrackedNav(e, c.url)}
          className="ah-block flex items-center justify-center text-center min-w-0"
          data-testid={`block-embed-${block.block_id}`}
        >
          <span className="font-bold text-sm truncate block min-w-0 max-w-full">{c.label || c.url}</span>
        </a>
      );
    }
    return <p className="text-xs ah-muted text-center">Embed (no URL set)</p>;
  }

  if (type === "spacer") {
    const h = Number(c.height) || 24;
    return <div style={{ height: `${Math.max(4, Math.min(120, h))}px` }} data-testid={`block-spacer-${block.block_id}`} />;
  }

  // ---------- Phase 3 advanced blocks ----------
  if (type === "product") {
    return <ProductBlock block={block} onClick={handleTrackedNav} editable={editable} hubContext={hubContext} />;
  }

  if (type === "lead_form") {
    return (
      <CollapsibleFormWrap
        block={block}
        editable={editable}
        ctaLabel={c.cta_label || c.headline || "Get in touch"}
        ctaIcon={Send}
        subtitle={c.cta_subtext}
        testid={`block-lead-form-wrap-${block.block_id}`}
      >
        <LeadFormBlock block={block} editable={editable} />
      </CollapsibleFormWrap>
    );
  }

  if (type === "coupon") {
    return <CouponBlock block={block} editable={editable} hubContext={hubContext} />;
  }
  if (type === "paid_coupon") {
    // v143.13 — Route the block's CTA tap through the shared click
    // tracker so per-block Results shows non-zero for paid_coupon.
    return <PaidCouponBlock block={block} editable={editable} onClick={handleTrackedNav} hubContext={hubContext} />;
  }
  if (type === "referral") {
    return <ReferralBlock block={block} editable={editable} hubContext={hubContext} />;
  }

  if (type === "countdown") {
    return <CountdownBlock block={block} />;
  }

  if (type === "faq") {
    const items = c.items || [];
    return (
      <div className="space-y-2" data-testid={`block-faq-${block.block_id}`}>
        {items.map((it, i) => (
          <FAQItem key={i} q={it.q} a={it.a} testid={`block-faq-item-${block.block_id}-${i}`} />
        ))}
        {items.length === 0 && <p className="text-xs ah-muted text-center">Add FAQ items in the editor.</p>}
      </div>
    );
  }

  if (type === "testimonial") {
    return (
      <div className="ah-block" data-testid={`block-testimonial-${block.block_id}`}>
        <div className="flex items-center gap-1 mb-2 justify-center">
          {[...Array(Math.max(0, Math.min(5, Number(c.rating) || 5)))].map((_, i) => (
            <Star key={i} className="w-4 h-4" fill="currentColor" strokeWidth={1} />
          ))}
        </div>
        <p className="text-sm italic text-center leading-relaxed">&ldquo;{c.quote || "Loved the product!"}&rdquo;</p>
        <p className="mt-2 text-xs font-bold ah-muted text-center">— {c.author || "Happy customer"}</p>
      </div>
    );
  }

  if (type === "video_playlist") {
    const items = (c.items || []).filter((v) => v && v.url);
    if (!items.length) return <p className="text-xs ah-muted text-center">Add videos in the editor.</p>;
    return (
      <VideoPlaylistBlock block={block} items={items} />
    );
  }

  if (type === "poll") {
    return <PollBlock block={block} editable={editable} />;
  }

  if (type === "newsletter") {
    return <NewsletterBlock block={block} editable={editable} />;
  }

  if (type === "whatsapp") {
    return <WhatsAppBlock block={block} onClick={handleTrackedNav} editable={editable} />;
  }

  if (type === "telegram") {
    return <TelegramBlock block={block} onClick={handleTrackedNav} editable={editable} />;
  }

  if (type === "app_download") {
    return <AppDownloadBlock block={block} onClick={handleTrackedNav} editable={editable} />;
  }

  // v147.16 — Branded restaurant ordering blocks. Global kill-switches
  // (`block_swiggy_order_enabled` / `block_zomato_order_enabled` /
  // `block_ownly_order_enabled`) let
  // super-admin hide even a fully-configured block from the public
  // renderer for instant rollback. Editors keep seeing the placeholder
  // so restaurants know the block exists (and get the "URL not set"
  // hint when appropriate).
  if (type === "swiggy_order") {
    if (!editable && flag("block_swiggy_order_enabled") === false) return null;
    return <SwiggyOrderBlock block={block} onClick={handleTrackedNav} editable={editable} />;
  }
  if (type === "zomato_order") {
    if (!editable && flag("block_zomato_order_enabled") === false) return null;
    return <ZomatoOrderBlock block={block} onClick={handleTrackedNav} editable={editable} />;
  }
  if (type === "ownly_order") {
    if (!editable && flag("block_ownly_order_enabled") === false) return null;
    return <OwnlyOrderBlock block={block} onClick={handleTrackedNav} editable={editable} />;
  }

  if (type === "appointment") {
    return (
      <CollapsibleFormWrap
        block={block}
        editable={editable}
        ctaLabel={c.cta_label || c.headline || "Book an appointment"}
        ctaIcon={Calendar}
        subtitle={c.cta_subtext}
        testid={`block-appointment-wrap-${block.block_id}`}
      >
        <AppointmentBlock block={block} editable={editable} />
      </CollapsibleFormWrap>
    );
  }

  if (type === "enquiry") {
    return (
      <CollapsibleFormWrap
        block={block}
        editable={editable}
        ctaLabel={c.cta_label || c.headline || "Make an enquiry"}
        ctaIcon={Mail}
        subtitle={c.cta_subtext}
        testid={`block-enquiry-wrap-${block.block_id}`}
      >
        <EnquiryBlock block={block} editable={editable} />
      </CollapsibleFormWrap>
    );
  }

  if (type === "location") {
    return <LocationBlock block={block} editable={editable} onClick={handleTrackedNav} />;
  }

  if (type === "brand_collab") {
    return <BrandCollabBlock block={block} editable={editable} onClick={handleTrackedNav} />;
  }

  // v145.47 — Digital business card (name / title / phone / WhatsApp / email
  // with vCard QR + save-contact download).
  if (type === "contact_card") {
    return <ContactCardBlock block={block} editable={editable} />;
  }

  // v145.48 — Image grid (menu / catalog / portfolio thumbnails).
  if (type === "image_grid") {
    return <ImageGridBlock block={block} editable={editable} />;
  }

  // v145.50 — Video grid (embed-only reels).
  if (type === "video_grid") {
    return <VideoGridBlock block={block} editable={editable} />;
  }

  // v45 Phase C — community & commerce blocks
  if (type === "event") {
    return <EventBlock block={block} editable={editable} kind="event" onClick={handleTrackedNav} />;
  }
  if (type === "meetup") {
    return <EventBlock block={block} editable={editable} kind="meetup" onClick={handleTrackedNav} />;
  }
  if (type === "ticket_booking") {
    return (
      <CollapsibleFormWrap
        block={block}
        editable={editable}
        ctaLabel={c.cta_label || c.headline || "Book tickets"}
        ctaIcon={Tag}
        subtitle={c.cta_subtext}
        testid={`block-ticket-booking-wrap-${block.block_id}`}
      >
        <TicketBookingBlock block={block} editable={editable} onClick={handleTrackedNav} />
      </CollapsibleFormWrap>
    );
  }
  if (type === "registration") {
    return (
      <CollapsibleFormWrap
        block={block}
        editable={editable}
        ctaLabel={c.cta_label || c.headline || "Register"}
        ctaIcon={ChevronDown}
        subtitle={c.cta_subtext}
        testid={`block-registration-wrap-${block.block_id}`}
      >
        <RegistrationBlock block={block} editable={editable} onClick={handleTrackedNav} />
      </CollapsibleFormWrap>
    );
  }
  if (type === "subscription") {
    return (
      <CollapsibleFormWrap
        block={block}
        editable={editable}
        ctaLabel={c.cta_label || c.headline || "Subscribe"}
        ctaIcon={Send}
        subtitle={c.cta_subtext}
        testid={`block-subscription-wrap-${block.block_id}`}
      >
        <SubscriptionBlock block={block} editable={editable} onClick={handleTrackedNav} />
      </CollapsibleFormWrap>
    );
  }

  if (type === "location_consent") {
    return <LocationConsentBlock block={block} editable={editable} onClick={handleTrackedNav} />;
  }

  if (type === "google_review") {
    return <GoogleReviewBlock block={block} editable={editable} onClick={handleTrackedNav} />;
  }

  if (type === "page_gate") {
    return <PageGateBlock block={block} editable={editable} onUnlock={onGateUnlock} />;
  }

  if (type === "custom_html") {
    if (editable) {
      return (
        <div className="ah-block text-xs ah-muted text-center" data-testid={`block-custom-html-preview-${block.block_id}`}>
          <p className="font-bold">Custom HTML block</p>
          <p className="ah-muted">{(c.html || "").length} chars — renders on public page.</p>
        </div>
      );
    }
    return (
      <div
        className="ah-block"
        data-testid={`block-custom-html-${block.block_id}`}
        dangerouslySetInnerHTML={{ __html: c.html || "" }}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // v42: Hub-only blocks (used on /u/{slug} bio page). These pull their data
  // from `hubContext` prop passed by PublicCreatorDirectory / hub editor.
  // In the editor, hubContext=null; we render a placeholder preview instead.
  // ---------------------------------------------------------------------------
  if (type === "avatar") {
    const size = c.size === "xl" ? "w-32 h-32" : c.size === "lg" ? "w-24 h-24" : c.size === "sm" ? "w-14 h-14" : "w-20 h-20";
    const creator = hubContext?.creator;
    const showCover = !!c.show_cover && !!creator?.cover_url;
    const showBio = !!c.show_bio && !!creator?.bio;
    if (showCover) {
      return (
        <div className="ah-block" data-testid={`block-avatar-${block.block_id}`}>
          <div className="relative border-2 border-ink shadow-brutalSm overflow-hidden">
            <div className="h-28 sm:h-36 w-full bg-gradient-to-br from-mint via-sun to-lavender relative">
              <img src={sizedImage(creator.cover_url, 960)} alt=""
                   className="absolute inset-0 w-full h-full object-cover"
                   loading="eager" decoding="async" fetchPriority="high"
                   data-testid={`block-avatar-cover-${block.block_id}`} />
            </div>
            {/* z-10 so the avatar circle sits ABOVE the absolute-positioned
                cover img — without it, the cover would render on top of a
                statically-positioned sibling in the same stacking context. */}
            <div className="relative z-10 flex justify-center -mt-10 sm:-mt-12">
              {creator?.picture ? (
                <img src={sizedImage(creator.picture, 320)} alt=""
                     className={`${size} rounded-full border-2 border-ink shadow-brutalSm object-cover bg-white`}
                     loading="eager" decoding="async" />
              ) : (
                <div className={`${size} rounded-full border-2 border-ink shadow-brutalSm bg-coral flex items-center justify-center text-white font-display font-black text-4xl`}>
                  {((creator?.name || creator?.slug || "?").charAt(0)).toUpperCase()}
                </div>
              )}
            </div>
            <div className="text-center px-4 pt-2 pb-4 bg-white">
              {c.show_name !== false && creator && (
                <p className="font-display font-black text-2xl sm:text-3xl tracking-tighter">
                  {creator.name || `@${creator.slug}`}
                </p>
              )}
              {c.show_handle && (creator?.instagram_handle) && (
                <p className="text-sm ah-muted mt-1">@{(creator.instagram_handle || "").replace(/^@/, "")}</p>
              )}
              {showBio && (
                <p className="text-sm ah-muted mt-2 max-w-md mx-auto"
                   data-testid={`block-avatar-bio-${block.block_id}`}>{creator.bio}</p>
              )}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="ah-block text-center" data-testid={`block-avatar-${block.block_id}`}>
        {creator?.picture ? (
          <img src={sizedImage(creator.picture, 320)} alt=""
               className={`${size} rounded-full border-2 border-ink mx-auto shadow-brutalSm object-cover`}
               loading="eager" decoding="async" />
        ) : (
          <div className={`${size} rounded-full border-2 border-ink mx-auto shadow-brutalSm bg-coral flex items-center justify-center text-white font-display font-black text-4xl`}>
            {((creator?.name || creator?.slug || "?").charAt(0)).toUpperCase()}
          </div>
        )}
        {c.show_name !== false && creator && (
          <p className="font-display font-black text-2xl sm:text-3xl tracking-tighter mt-3">
            {creator.name || `@${creator.slug}`}
          </p>
        )}
        {c.show_handle && (creator?.instagram_handle) && (
          <p className="text-sm ah-muted mt-1">@{(creator.instagram_handle || "").replace(/^@/, "")}</p>
        )}
        {showBio && (
          <p className="text-sm ah-muted mt-2 max-w-md mx-auto"
             data-testid={`block-avatar-bio-${block.block_id}`}>{creator.bio}</p>
        )}
      </div>
    );
  }

  if (type === "pages_grid") {
    const allPages = hubContext?.pages || [];
    // v53.4 — respect an explicit `page_ids` config so creators can curate
    // which pages appear. Empty/missing means "show all" (legacy behaviour).
    const ids = Array.isArray(c.page_ids) ? c.page_ids : [];
    const pages = ids.length > 0
      ? ids.map((id) => allPages.find((p) => p.page_id === id)).filter(Boolean)
      : allPages;
    // v146.32 — Layout adapts to the column count:
    //  - 1 col : horizontal card (image left · text middle · Visit right)
    //  - 2+ col: vertical card (image top · text middle · Visit bottom)
    // The old flex-row layout crammed everything horizontally on 2-cols
    // so text wrapped weirdly and Visit overlapped the title on mobile.
    const isMultiCol = c.columns !== 1;
    const cols = c.columns === 1
      ? "grid-cols-1"
      : c.columns === 3
        ? "grid-cols-2 sm:grid-cols-3"
        : "grid-cols-2";
    if (editable && pages.length === 0) {
      return (
        <div className="ah-block border-2 border-dashed border-ink/40 p-4 text-center text-xs ah-muted"
             data-testid={`block-pages-grid-preview-${block.block_id}`}>
          <p className="font-bold text-sm ah-body">Explore Pages grid</p>
          <p>Every published Action Page you create appears here automatically.</p>
        </div>
      );
    }
    return (
      <div className="ah-block" data-testid={`block-pages-grid-${block.block_id}`}>
        {c.heading && (
          <p className="text-xs uppercase tracking-wider font-bold ah-muted mb-3">{c.heading}</p>
        )}
        <div className={`grid gap-3 ${cols}`}>
          {pages.map((p, i) => {
            // v146.7 — Per-page thumbnail override: creators can pick
            // a bespoke square via the pages_grid editor. Falls back
            // to the backend-derived cover (first hero_image block)
            // so pages without an override still get a thumbnail if
            // one exists on the page.
            const perBlockThumb = (c.thumbnails && typeof c.thumbnails === "object")
              ? c.thumbnails[p.page_id]
              : null;
            const coverSrc = perBlockThumb || p.cover_image_url || null;
            return (
            <a key={p.page_id}
               href={hubContext?.pageHref ? hubContext.pageHref(p) : "#"}
               onClick={(e) => hubContext?.onPageClick && hubContext.onPageClick(e, p)}
               className={`group border-2 border-ink bg-white p-3 shadow-brutalSm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] hover:bg-cream active:translate-x-[2px] active:translate-y-[2px] transition-all ${
                 isMultiCol
                   ? "flex flex-col gap-2 min-w-0"
                   : "flex items-center gap-3"
               }`}
               data-testid={`hub-page-card-${i}`}>
              {coverSrc ? (
                <img
                  src={sizedImage(coverSrc, 320)}
                  alt=""
                  loading="lazy"
                  className={
                    isMultiCol
                      ? "w-full aspect-square border-2 border-ink object-cover shrink-0"
                      : "w-14 h-14 sm:w-16 sm:h-16 shrink-0 border-2 border-ink object-cover"
                  }
                  data-testid={`hub-page-card-cover-${i}`}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : null}
              <div className={`min-w-0 ${isMultiCol ? "" : "flex-1"}`}>
                <p className="font-bold text-base leading-snug group-hover:underline underline-offset-4 decoration-2 line-clamp-2">{p.title}</p>
                {p.description && <p className="text-xs ah-muted mt-0.5 line-clamp-2">{p.description}</p>}
              </div>
              {/* v146.5 — Explicit "Visit" affordance so the card
                  clearly reads as a link, not a static blurb. Pill uses
                  the coral CTA colour + arrow icon so the tap target
                  is obvious on mobile. v146.32 — In multi-column mode
                  the pill sits full-width at the bottom of the card
                  (image → text → button) rather than crammed onto the
                  right of a horizontal row. */}
              <span
                aria-hidden="true"
                className={`shrink-0 inline-flex items-center gap-1 border-2 border-ink bg-coral text-white px-2.5 py-1 text-[11px] font-black uppercase tracking-wider shadow-brutalSm group-hover:shadow-none ${
                  isMultiCol ? "self-start mt-auto" : ""
                }`}
                data-testid={`hub-page-card-visit-${i}`}
              >
                Visit
                <ArrowUpRight className="w-3 h-3" strokeWidth={3} />
              </span>
            </a>
            );
          })}
          {pages.length === 0 && (
            <p className="text-xs ah-muted col-span-full">No published pages yet.</p>
          )}
        </div>
      </div>
    );
  }

  if (type === "products_grid" || type === "storefront") {
    const allProducts = hubContext?.products || [];
    const ids = Array.isArray(c.product_ids) ? c.product_ids : [];
    // v149.70 — `referenced_only` products came along because some block
    // names them; a grid of "all products" doesn't list them.
    const products = ids.length > 0
      ? ids.map((id) => allProducts.find((p) => p.product_id === id)).filter(Boolean)
      : allProducts.filter((p) => !p.referenced_only);
    // v145.23 — Honour the creator's chosen column count on mobile too.
    // Previously the base class was `grid-cols-1` implicitly, so mobile
    // collapsed to one column even when 3 or 4 was picked. Now the
    // mobile default matches the selection (2/3/4) so cards render as
    // configured across viewports; desktop scales up to the same count.
    // v146.1 — coerce to Number: legacy blocks stored `columns` as
    // string ("2") and the strict-equality check silently fell through.
    const pgCols = Number(c.columns) || 3;
    const cols = pgCols === 1
      ? "grid-cols-1"
      : pgCols === 2
        ? "grid-cols-2"
        : pgCols === 4
          ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
          : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-3";
    // v149.69 — "Open my store as its own page": one button to
    // `/store/{slug}` instead of the products listed here.
    if (c.display === "link") {
      return (
        <StoreLinkBlock
          block={block}
          slug={hubContext?.creator?.slug || hubContext?.slug || null}
          products={products}
          onNavigate={handleTrackedNav}
        />
      );
    }
    if (editable && products.length === 0) {
      return (
        <div className="ah-block border-2 border-dashed border-ink/40 p-4 text-center text-xs ah-muted"
             data-testid={`block-storefront-preview-${block.block_id}`}>
          <p className="font-bold text-sm ah-body">Storefront</p>
          <p>Products &amp; services from your catalog appear here — buyers get the same checkout experience as your /store page.</p>
        </div>
      );
    }
    // v113 — Reuse the same StorefrontCard as the standalone
    // /store/{slug} page so services show a "Book" CTA, the buy modal
    // supports date+slot pickers, coupons, address collection, OTP, and
    // instructions/terms — identical experience whether the buyer clicked
    // through the storefront link, a bio-page block, or an action-page
    // block.
    const slug = hubContext?.creator?.slug || hubContext?.slug || null;
    const requireOtp = !!(hubContext?.settings?.require_phone_otp);
    const otpChannel = hubContext?.settings?.otp_channel || "auto";
    // v147.66 — Surface COD availability inside bio-hub / action-page
    // storefront blocks so the buy modal shows the "Pay Now / Cash on
    // Delivery" toggle when the creator has enabled COD in Settings →
    // Commerce & Payments. Before this, bio-hub buyers were always
    // force-routed through Razorpay even when the /store/{slug} page
    // offered COD for the same product.
    const codEnabled = !!(hubContext?.settings?.cod_enabled);
    // v149.70 — False only for a "Cash on Delivery only" shop.
    const onlineEnabled = hubContext?.settings?.online_enabled !== false;
    // v145.27 — Cart on bio-hub / action-page storefront blocks too.
    // Block config controls whether phone / address are required at
    // final checkout. Provider wraps just this block so the cart is
    // scoped to that section.
    return (
      <StorefrontCartProvider slug={slug}>
      <div className="ah-block" data-testid={`block-storefront-${block.block_id}`}>
        {c.heading && <p className="text-xs uppercase tracking-wider font-bold ah-muted mb-3">{c.heading}</p>}
        <div className={`grid gap-3 ${cols}`}>
          {products.slice(0, c.limit || 12).map((p, i) => (
            <StorefrontCard
              key={p.product_id}
              product={p}
              index={i}
              slug={slug}
              requireOtp={requireOtp}
              otpChannel={otpChannel}
              codEnabled={codEnabled}
              onlineEnabled={onlineEnabled}
              requirePhone={!!c.require_phone}
              requireAddress={!!c.require_address}
              deliveryCharge={Number(c.delivery_charge) || 0}
            />
          ))}
          {products.length === 0 && (
            <p className="text-xs ah-muted col-span-full">No products yet.</p>
          )}
        </div>
        {!editable && (
          <StorefrontCartBar
            slug={slug}
            buyerUser={null}
            requirePhone={!!c.require_phone}
            requireAddress={!!c.require_address}
            deliveryCharge={Number(c.delivery_charge) || 0}
            codEnabled={codEnabled}
            onlineEnabled={onlineEnabled}
          />
        )}
      </div>
      </StorefrontCartProvider>
    );
  }

  if (type === "featured_product") {
    return (
      <FeaturedProductBlock block={block} hubContext={hubContext} editable={editable} />
    );
  }

  if (type === "affiliate_picks") {
    // v145.64 — Action-page-only block. Reads from
    // `hubContext.affiliate_products` (server-hydrated with pick_id,
    // customer_discount_pct, social_proof_count, brand_name).
    // Regular products_grid blocks no longer include picks.
    const all = Array.isArray(hubContext?.affiliate_products)
      ? hubContext.affiliate_products
      : [];
    // v145.65 — Per-block allowlist. Empty ⇒ show all. Sentinel
    // `["__none__"]` ⇒ show nothing (creator explicitly cleared).
    const pickIds = Array.isArray(c.pick_ids) ? c.pick_ids : [];
    let items;
    if (pickIds.length === 0) {
      items = all;
    } else if (pickIds.length === 1 && pickIds[0] === "__none__") {
      items = [];
    } else {
      const allow = new Set(pickIds);
      items = all.filter((p) => allow.has(p.pick_id));
    }
    // v146.1 — coerce to Number and support 1-column layout. Legacy
    // blocks stored `columns` as string ("2"), so the strict-equality
    // check silently fell through to the 3-column default.
    const apCols = Number(c.columns) || 3;
    const cols = apCols === 1
      ? "grid-cols-1"
      : apCols === 2
        ? "grid-cols-2"
        : apCols === 4
          ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
          : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-3";
    if (editable && items.length === 0) {
      return (
        <div className="ah-block border-2 border-dashed border-ink/40 p-4 text-center text-xs ah-muted"
             data-testid={`block-affiliate-picks-preview-${block.block_id}`}>
          <p className="font-bold text-sm ah-body">Affiliate Products</p>
          <p>
            Products you pick from the{" "}
            <a href="/app/affiliate-marketplace" className="underline font-bold">Affiliate Marketplace</a>{" "}
            will appear here.
          </p>
        </div>
      );
    }
    const slug = hubContext?.creator?.slug || hubContext?.slug || null;
    const requireOtp = !!(hubContext?.settings?.require_phone_otp);
    const otpChannel = hubContext?.settings?.otp_channel || "auto";
    const codEnabled = !!(hubContext?.settings?.cod_enabled);
    // v149.70 — Picks are other sellers' products, settled online with the
    // affiliate: this page's own "Cash on Delivery only" setting isn't applied.
    return (
      <div className="ah-block" data-testid={`block-affiliate-picks-${block.block_id}`}>
        {c.heading && (
          <p className="text-xs uppercase tracking-wider font-bold ah-muted mb-3">
            {c.heading}
          </p>
        )}
        <div className={`grid gap-3 ${cols}`}>
          {items.slice(0, c.limit || 12).map((p, i) => (
            <StorefrontCard
              key={p.product_id}
              product={p}
              index={i}
              slug={slug}
              requireOtp={requireOtp}
              otpChannel={otpChannel}
              codEnabled={codEnabled}
              requirePhone={!!c.require_phone}
              requireAddress={!!c.require_address}
              deliveryCharge={Number(c.delivery_charge) || 0}
            />
          ))}
          {items.length === 0 && (
            <p className="text-xs ah-muted col-span-full">No affiliate picks yet.</p>
          )}
        </div>
      </div>
    );
  }

  // v149.88 — My Screen Playback (the host's Smart Screen on their page).
  if (type === "screen_playback") {
    return <ScreenPlaybackBlock block={block} hubContext={hubContext} editable={editable} />;
  }

  // v149 — Quick Promote ads block (Linkola-served local ads).
  if (type === "ads") {
    return <AdsBlock block={block} hubContext={hubContext} editable={editable} />;
  }

  // v147.8 — Fallback for unknown block types. In the creator editor we
  // show a small hint so devs / creators know the block is unrecognised;
  // on public / visitor renders we render nothing so end users never see
  // developer strings for legacy block types that were deprecated.
  if (editable) {
    return <p className="text-xs ah-muted" data-testid={`block-unknown-${block.block_id}`}>Unknown block type: {type}</p>;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Phase 3 block internals
// ---------------------------------------------------------------------------

// v119 — Block components live in dedicated files under `actionBlocks/`.
// Re-exported here so every existing import site (e.g.
// `import { ProductBlock } from "./ActionBlockRenderer"`) keeps working
// with zero churn. The main file above hosts only the top-level dispatch
// switch (link/header/text/socials/hero/image/avatar/…grid) — every
// heavy block sits in its category file.
// -- imported for local use inside the dispatch switch above --
import {
  ProductBlock,
  LeadFormBlock,
  CouponBlock,
  FeaturedProductBlock,
  BuyNowGridButton,
  ProductBuyModal,
  PaidCouponCheckout,
  PaidCouponBlock,
} from "./actionBlocks/commerce";
import {
  CountdownBlock,
  FAQItem,
  VideoPlaylistBlock,
  PollBlock,
  NewsletterBlock,
  ReferralBlock,
} from "./actionBlocks/engagement";
import {
  WhatsAppBlock,
  TelegramBlock,
  AppDownloadBlock,
  AppointmentBlock,
  EnquiryBlock,
  LocationBlock,
} from "./actionBlocks/contact";
import { SwiggyOrderBlock, ZomatoOrderBlock, OwnlyOrderBlock } from "./actionBlocks/orderPlatforms";
import AdsBlock from "./actionBlocks/AdsBlock";
import ScreenPlaybackBlock from "./actionBlocks/ScreenPlaybackBlock";
import StoreLinkBlock from "./actionBlocks/StoreLinkBlock";
import { useAppFlags } from "../lib/useAppFlags";
import {
  GoogleReviewBlock,
  PageGateBlock,
} from "./actionBlocks/gate";

// -- re-exports so existing import sites keep working with zero churn --
export {
  ProductBlock,
  LeadFormBlock,
  CouponBlock,
  FeaturedProductBlock,
  BuyNowGridButton,
  ProductBuyModal,
  PaidCouponCheckout,
  PaidCouponBlock,
};
export {
  CountdownBlock,
  FAQItem,
  VideoPlaylistBlock,
  PollBlock,
  NewsletterBlock,
  ReferralBlock,
};
export {
  WhatsAppBlock,
  TelegramBlock,
  AppDownloadBlock,
  AppointmentBlock,
  EnquiryBlock,
  LocationBlock,
};
export {
  GoogleReviewBlock,
  PageGateBlock,
};
export { SwiggyOrderBlock, ZomatoOrderBlock, OwnlyOrderBlock };
